import React, { createContext, useContext, useState, useEffect } from 'react';
import { useCurrentProject } from '../hooks/useProject';
import { useAuth } from './AuthContext';

const ProjectContext = createContext({});

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjectContext must be used within ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { currentUser, userRole } = useAuth();
  const {
    project,
    projectId,
    loading,
    error,
    saving,
    lastSaved,
    updateHubTool,
    addActivity,
    createProject
  } = useCurrentProject();

  // Check if user has access to projects
  const hasProjectAccess = userRole && (
    userRole.includes('user_paid') ||
    userRole.includes('user_dynamic') ||
    userRole === 'user_pro' ||
    userRole === 'admin'
  );

  // Helper to check if a specific tool is unlocked
  const isToolUnlocked = (toolName) => {
    if (!hasProjectAccess) return false;
    
    // All tools unlocked for pro and admin
    if (userRole === 'user_pro' || userRole === 'admin') return true;
    
    // Tool-specific logic based on tier
    const tier = project?.tier;
    
    const tier2Tools = ['budget', 'tasks', 'activities'];
    const tier3Tools = [...tier2Tools, 'strategy', 'dataTools', 'notes'];
    
    if (tier === 2) return tier2Tools.includes(toolName);
    if (tier === 3) return tier3Tools.includes(toolName);
    
    return false;
  };

  // Auto-save indicator
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);
  
  useEffect(() => {
    if (saving) {
      setShowSaveIndicator(true);
    } else if (lastSaved) {
      // Show "Saved" for 2 seconds after save completes
      setTimeout(() => setShowSaveIndicator(false), 2000);
    }
  }, [saving, lastSaved]);

  const value = {
    // Project data
    project,
    projectId,
    loading,
    error,
    
    // Save state
    saving,
    lastSaved,
    showSaveIndicator,
    
    // Access control
    hasProjectAccess,
    isToolUnlocked,
    
    // Actions
    updateHubTool,
    addActivity,
    createProject,
    
    // Convenience methods
    updateBudget: (budgetData) => updateHubTool('budget', budgetData),
    updateTasks: (tasksData) => updateHubTool('tasks', tasksData),
    updateStrategy: (strategyData) => updateHubTool('strategy', strategyData),
    updateNotes: (notesData) => updateHubTool('notes', notesData),
    
    // Get specific tool data
    getBudgetData: () => project?.hubData?.budget || null,
    getTasksData: () => project?.hubData?.tasks || [],
    getActivitiesData: () => project?.hubData?.activities || [],
    getStrategyData: () => project?.hubData?.strategy || null,
    getNotesData: () => project?.hubData?.notes || []
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
      
      {/* Global save indicator */}
      {showSaveIndicator && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          background: saving ? '#2196F3' : '#4CAF50',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 9999,
          animation: 'slideIn 0.3s ease-out'
        }}>
          <span className="material-icons" style={{ fontSize: '18px' }}>
            {saving ? 'sync' : 'check_circle'}
          </span>
          {saving ? 'Saving...' : 'Saved'}
        </div>
      )}
      
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </ProjectContext.Provider>
  );
};